<template>
  <div class="portfolio-page">

    <div class="page-header">
      <div class="page-header-grid"></div>
      <div class="container page-header-content">
        <router-link to="/" class="back-link">
          <i class="fas fa-arrow-left"></i> {{ $t('portfolio_page.back') }}
        </router-link>
        <h1>Portfolio</h1>
        <p>{{ $t('portfolio_page.subtitle') }}</p>
      </div>
    </div>

    <div class="projects-section">
      <div class="container">

        <div class="filter-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            class="filter-tab"
            :class="{ active: activeFilter === tab.value }"
            @click="activeFilter = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="row g-4">
          <div
            v-for="project in filteredProjects"
            :key="project.id"
            class="col-lg-4 col-md-6"
          >
            <div class="project-card" v-tilt="4">
              <a
                :href="project.url || null"
                :target="project.url ? '_blank' : null"
                :rel="project.url ? 'noopener noreferrer' : null"
                class="project-image"
                :class="[project.gradient, { 'no-link': !project.url }]"
              >
                <img
                  v-if="project.src"
                  :src="project.src"
                  :alt="project.title"
                  class="project-screenshot"
                  @error="e => e.target.style.display='none'"
                />
                <span v-if="project.comingSoon" class="coming-soon-badge">Coming soon</span>
                <div class="project-overlay">
                  <i class="fas fa-external-link-alt"></i>
                </div>
              </a>
              <div class="project-body">
                <div class="project-meta">
                  <span class="project-category">{{ $t(`portfolio_page.category_${project.category}`) }}</span>
                </div>
                <h2>{{ project.title }}</h2>
                <p>{{ project.description[locale] }}</p>
                <div class="project-tags">
                  <span v-for="tag in project.tags" :key="tag">{{ tag }}</span>
                </div>
                <a
                  v-if="project.url"
                  :href="project.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn-project"
                >
                  {{ $t('portfolio_page.view_project') }} <i class="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div v-if="filteredProjects.length === 0" class="empty-state">
          <i class="fas fa-folder-open"></i>
          <p>{{ $t('portfolio_page.empty') }}</p>
        </div>

      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { projects } from '@/data/projects'

const { locale, t } = useI18n()

const activeFilter = ref('all')

const tabs = computed(() => [
  { label: t('portfolio_page.filter_all'),     value: 'all' },
  { label: t('portfolio_page.filter_web'),     value: 'web' },
  { label: t('portfolio_page.filter_mobile'),  value: 'mobile' },
  { label: t('portfolio_page.filter_backend'), value: 'backend' },
])

const filteredProjects = computed(() =>
  activeFilter.value === 'all'
    ? projects
    : projects.filter((p) => p.category === activeFilter.value)
)
</script>

<style scoped>
.page-header {
  background-color: transparent;
  padding: 8rem 0 4rem;
  position: relative;
  overflow: hidden;
}

.page-header-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

.page-header-content { position: relative; z-index: 1; }

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.45);
  text-decoration: none;
  margin-bottom: 1.75rem;
  transition: color 0.2s;
}

.back-link:hover { color: rgba(255, 255, 255, 0.8); text-decoration: none; }

.page-header h1 {
  font-family: 'Jersey 15', sans-serif;
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  color: #fff;
  font-weight: 400;
  margin-bottom: 0.75rem;
  letter-spacing: 0;
}

.page-header p { color: rgba(255, 255, 255, 0.5); font-size: 1.05rem; margin: 0; }

.projects-section {
  padding: 4rem 0 6rem;
  background: linear-gradient(180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.5) 12%,
    rgba(0, 0, 0, 0.5) 88%,
    rgba(0, 0, 0, 0) 100%);
  min-height: 60vh;
}

.filter-tabs { display: flex; gap: 0.5rem; margin-bottom: 2.5rem; flex-wrap: wrap; }

.filter-tab {
  padding: 0.55rem 1.35rem;
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-muted);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s, box-shadow 0.2s;
}

.filter-tab:hover { border-color: var(--accent-line); color: var(--accent); }
.filter-tab.active {
  background: var(--accent-grad);
  border-color: transparent;
  color: #0a0a0a;
  font-weight: 700;
  box-shadow: 0 4px 18px var(--accent-glow);
}

.project-card {
  background: linear-gradient(180deg, #151515, #0e0e0e);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s, border-color 0.3s;
  transform-style: preserve-3d;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.project-card:hover { box-shadow: 0 24px 56px rgba(0, 0, 0, 0.6), 0 0 36px var(--accent-tint); border-color: var(--accent-line); }

.project-image { height: 180px; position: relative; overflow: hidden; display: block; cursor: pointer; }
.project-image.no-link { cursor: default; pointer-events: none; }

/* Luxe light-sweep across the screenshot on hover */
.project-image::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -80%;
  width: 55%;
  background: linear-gradient(105deg, transparent, rgba(255, 255, 255, 0.18), transparent);
  transform: skewX(-16deg);
  transition: left 0.65s ease;
  pointer-events: none;
  z-index: 1;
}
.project-card:hover .project-image::after { left: 130%; }

.coming-soon-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(10, 10, 10, 0.78);
  color: var(--accent-soft);
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0.25rem 0.6rem;
  border-radius: 5px;
  border: 1px solid var(--accent-line);
  z-index: 2;
  backdrop-filter: blur(4px);
}

.project-screenshot {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* On-brand fallbacks, only visible when a screenshot fails to load */
.gradient-1 { background: linear-gradient(135deg, #1a1206 0%, #7a5416 50%, #FAA82B 100%); }
.gradient-2 { background: linear-gradient(135deg, #0e0e0e 0%, #4a3510 50%, #e08a12 100%); }
.gradient-3 { background: linear-gradient(135deg, #201607 0%, #8a6018 50%, #ffc369 100%); }

.project-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s;
}

.project-overlay i { font-size: 1.5rem; color: #fff; }
.project-card:hover .project-overlay { opacity: 1; }

.project-body { padding: 1.625rem 1.75rem; flex: 1; display: flex; flex-direction: column; }

.project-meta { margin-bottom: 0.6rem; }

.project-category {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.72rem;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

.project-body h2 { font-size: 1.15rem; color: var(--text); font-weight: 700; margin-bottom: 0.75rem; }

.project-body p { color: var(--text-muted); font-size: 0.92rem; line-height: 1.7; margin-bottom: 1.25rem; flex: 1; }

.project-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1.5rem; }

.project-tags span {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.7rem;
  color: var(--text-soft);
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 0.2rem 0.55rem;
}

.btn-project {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--accent);
  text-decoration: none;
  transition: gap 0.2s, color 0.2s;
}

.btn-project:hover { color: var(--accent-soft); gap: 0.65rem; text-decoration: none; }
.btn-project i { font-size: 0.75rem; transition: transform 0.2s; }
.btn-project:hover i { transform: translateX(2px); }

.empty-state { text-align: center; padding: 4rem 0; color: var(--text-dim); }
.empty-state i { font-size: 3rem; margin-bottom: 1rem; display: block; }
.empty-state p { font-size: 1rem; margin: 0; }

@media (max-width: 767px) {
  .page-header { padding: 6rem 0 3rem; }
  .projects-section { padding: 3rem 0 4rem; }
  .project-image { height: 160px; }
  .project-body { padding: 1.375rem 1.5rem; }
}
</style>
